// ─────────────────────────────────────────────────────────────────────────────
// LIH Recommendation Engine  v3.0
// Phase 3 — Real engine. Replaces stub.
// Sources: decisionBranches.json (all flags + rules), products.json (92 products),
//          science.json (29 entries), NIH engine logic (audited v2)
// ─────────────────────────────────────────────────────────────────────────────

import productsData from '../data/products.json';
import scienceData  from '../data/science.json';

// ── Lookup maps ───────────────────────────────────────────────────────────────

const PM = {}; // product map: id → product
productsData.forEach(p => { PM[p.id] = p; });

// Science map: productId → first relevant science entry
const SM = {};
scienceData.forEach(s => {
  (s.relevantProducts || []).forEach(pid => {
    if (!SM[pid]) SM[pid] = s;
  });
});

// ── Science summaries (coaching-voice, card-length) ───────────────────────────
// Written for clarity and coaching tone — references Grade/study where relevant.
const SCI = {
  'omegaguard':           'REDUCE-IT trial (NEJM 2019) and multiple meta-analyses confirm EPA+DHA reduces cardiovascular events, lowers triglycerides 20–30%, and supports anti-inflammatory signaling. One of the most evidence-dense supplement categories.',
  'vegan-omega-3':        'Algae-derived DHA+EPA delivers identical cardiovascular and cognitive benefits to fish oil — algae is the original source fish obtain their omega-3 from. Same mechanism, no animal involvement.',
  'vita-d3':              'NHANES data: 42% of Americans are vitamin D insufficient. D3 is essential for immune competence, bone density, mood regulation, and calcium absorption. Deficiency is among the most correctable and commonly missed nutritional gaps.',
  'vivix-original':       'Ghanim et al. 2011 (JCEM) — randomized controlled trial demonstrating polyphenol/resveratrol combination significantly suppresses post-meal oxidative stress and inflammatory gene expression. Grade A clinical evidence.',
  'vivix-kosher':         'Ghanim et al. 2011 (JCEM) — Grade A RCT. Kosher-certified formulation of the Vivix polyphenol/resveratrol complex.',
  'mindworks':            'B12 deficiency affects up to 30% of adults over 50 — a leading cause of reversible cognitive decline. The MindWorks Smart proprietary blend (Chardonnay grape, guarana, blueberry, green coffee) showed 3x better short-term memory responses vs. control at six hours in clinical testing per the product label.',
  'optiflora-di':         'Clinical evidence supports multi-strain probiotic supplementation for gut barrier integrity, immune function, and microbiome diversity. 10 billion CFU of 4 clinically validated strains is a meaningful therapeutic dose.',
  'dream-serene':         'Meta-analysis of 19 RCTs confirms melatonin significantly reduces sleep onset latency. Valerian root has Grade B evidence for sleep quality improvement. L-theanine adds alpha-wave induction for sleep-supportive neurological calming.',
  'rest-rewind':          '5-HTP is the direct precursor to serotonin, which is required for melatonin synthesis. Magnesium glycinate supports GABA receptor function — the primary neurological brake that chronic stress suppresses.',
  'stress-relief-complex':'Ashwagandha root extract has Grade A RCT evidence for cortisol reduction (Chandrasekhar et al. 2012: 27.9% reduction in serum cortisol vs. placebo). The L-theanine pairing addresses both acute and chronic stress pathways. Beta-sitosterol and L-tyrosine round out the calming formula.',
  'cheer-up-chill-out':   'Saffron extract has clinical evidence for mood support. Ashwagandha adds cortisol modulation. With B6 and B12 at 1,000% DV, the formula addresses both the neurotransmitter cofactor side and the stress-axis dimension of mood through complementary pathways.',
  'b-complex':            'B vitamins are cofactors in every step of neurotransmitter synthesis and energy metabolism. Chronic stress depletes B vitamins — deficiency directly impairs serotonin, dopamine, and GABA production.',
  'triple-defense-boost': 'D3 + C + Zinc — the combination with the strongest evidence base for immune support. Cochrane reviews confirm significant reductions in illness duration and severity for this nutrient combination.',
  'nutriferon':           'NutriFeron activates the body\'s natural interferon pathway — a distinct mechanism from standard D/C/Zinc. Clinical trial: 37% reduction in cold frequency. It\'s additive to, not redundant with, the Triple Defense stack.',
  'joint-health-complex': 'Glucosamine HCl 1,500mg provides cartilage matrix substrate (Cochrane review). Patented Boswellia AKBA Standardized Extract supports joint comfort within days. These are structural and modulating agents, not pain maskers.',
  'coqheart':             'CoQ10 levels decline with age and are further depleted by statins. As the primary mitochondrial ATP carrier, CoQ10 affects both cardiovascular function and cellular energy production. Grade A evidence for heart failure; strong Grade B for general cardiovascular support.',
  'cholesterol-reduction':'Plant sterols block cholesterol absorption at the gut level — the same mechanism behind the FDA-authorized health claim. Clinical trials consistently show 10–15% LDL reduction at 2g/day. Well-tolerated and non-systemic.',
  'blood-pressure':       'Blood Pressure Support Blend (1,300mg) combines potassium nitrate, quercetin from Sophora japonica, beet root powder, grape seed extract, and spinach leaf powder — supporting vasodilation via nitric oxide pathways and endothelial integrity via bioflavonoids. Label cautions: physician consultation advised for users with hypertension, cardiovascular disease, ED, or those taking BP medications.',
  'osteomatrix':          'The calcium/magnesium/D3/K2 combination represents the current evidence standard for bone mineral density support. K2 is the critical co-factor that directs calcium into bone rather than soft tissue — often absent from basic calcium supplements.',
  'iron-plus-c':          'Co-administration of Vitamin C with iron increases absorption 3–4x. Premenopausal women are the highest-risk group for iron depletion — it\'s the most common nutritional deficiency worldwide and among the most frequently missed causes of fatigue.',
  'eye-health':           'AREDS2 clinical trial — the gold-standard eye health RCT — showed 18% risk reduction in progression to advanced AMD with lutein/zeaxanthin supplementation. Lutein and zeaxanthin are selectively concentrated in the macular pigment.',
  'glucose-regulation':   'Chromium polynicotinate at 1,143% DV is the centerpiece — chromium has Grade B evidence for insulin sensitivity. Banaba leaf extract standardized to 18% corosolic acid adds a second glucose-uptake mechanism. Important: additive effects with diabetes medications require physician consultation.',
  'menopause-balance':    'Black cohosh has Grade B evidence for vasomotor symptoms (hot flashes) across multiple controlled trials. Soy isoflavones as selective estrogen receptor modulators have mixed but positive evidence for symptom management.',
  'gla-complex':          'GLA (gamma-linolenic acid) from evening primrose oil specifically addresses the inflammatory skin, dryness, and discomfort patterns associated with hormonal transition — covering a dimension that black cohosh alone does not reach.',
  'liver-dtox':           'Milk thistle extract 300mg has Grade A evidence for liver enzyme normalization in several clinical trials. The DTX Enhanced Proprietary Blend adds schisandra, dandelion, reishi, turmeric, and artichoke — a multi-herb approach to hepatic support and bile flow.',
  'carotomax':            'A broad-spectrum carotenoid complex — lutein, lycopene, beta-carotene, and zeaxanthin — covering the full eye and skin protective panel. Long-term NHANES data link carotenoid status to lower risk of macular degeneration and several cancers.',
  'electrolyte-plus':     'Sodium, potassium, magnesium, and chloride at clinically relevant ratios for hydration and neuromuscular function. Most athletes underestimate sodium replacement — it\'s the primary driver of performance-limiting dehydration.',
  'lifeshake-plant':      'Pea + rice protein combination delivers all essential amino acids and hits the leucine threshold (2.5–3g) required for muscle protein synthesis. 20g protein with a full micronutrient panel, versatile enough to work as a meal replacement or a straight supplement.',
  'advanced-physique-whey':'Cold-filtered grass-fed whey (Build) — BSCG Certified Drug-Free. Lower carbohydrate formula for strength training and body composition. It hits the leucine threshold for maximal muscle protein synthesis without the glycogen reload.',
  'build-plus':            'Build+ adds complex carbohydrates to the whey base for glycogen replenishment post-endurance training. Dr. Dick Brouse pilot data showed sedentary adults in their 80s achieved statistically significant muscle redevelopment using Build+ without structured exercise — supporting its use as a sarcopenia intervention regardless of activity level.',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getSci(id) {
  return SCI[id] || SM[id]?.summary || null;
}

// Build a product card from the catalog
function card(id, overrides = {}) {
  const p = PM[id];
  if (!p) {
    console.warn(`[LIH Engine] Product not found in catalog: ${id}`);
    return null;
  }
  return {
    name:     p.name,
    item:     `#${p.item}`,
    sku:      p.sku,
    serving:  p.serving,
    tier:     overrides.tier  ?? p.tier  ?? 2,
    category: p.category,
    why:      overrides.why   || p.rationale || '',
    science:  getSci(id),
    note:     overrides.note  || null,
  };
}

// ── Main export ───────────────────────────────────────────────────────────────

export function generateRecommendations(answers) {
  try {

    // ── PM validation ────────────────────────────────────────────────────────
    const pmSize = Object.keys(PM).length;
    console.log('[LIH] PM size:', pmSize);
    console.log('[LIH] vita-lea-gold-k in PM:', !!PM['vita-lea-gold-k']);
    console.log('[LIH] answers keys:', Object.keys(answers).join(', '));
    console.log('[LIH] q1_age:', answers.q1_age, 'q26_budget:', answers.q26_budget);

    if (pmSize === 0) {
      throw new Error('Product catalog failed to load — PM is empty. Check products.json import.');
    }

    const profile = buildProfile(answers);
    console.log('[LIH] profile.is50plus:', profile.is50plus, 'budgetModerate:', profile.budgetModerate, 'onBloodThinners:', profile.onBloodThinners);

    const { foundational, stripIncludes, onStrip } = buildFoundationalStack(profile);
    console.log('[LIH] foundational count:', foundational.length, 'onStrip:', onStrip);

    const modules = activateModules(profile, stripIncludes, onStrip);
    console.log('[LIH] modules count:', modules.length);

    return { foundational, modules, profile };
  
  } catch (err) {
    // Re-throw with enhanced context for debugging
    const enhancedMsg = `RECOMMENDATION ENGINE ERROR: ${err.message}\n\nStack: ${err.stack || 'No stack'}`;
    console.error(enhancedMsg);
    throw new Error(enhancedMsg);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PART 1 — PROFILE / FLAG DERIVATION
// Derives all 42 flags from raw assessment answers.
// Exact string matching preserved from decisionBranches.json v2.
// ─────────────────────────────────────────────────────────────────────────────

function buildProfile(a) {
  // ── Free-text normalization (runs before all other checks) ───────────────
  // Maps _other text fields to existing engine flags via keyword matching.
  // Lumen-only fields (q20, q15, q27) are passed through as-is; no engine mutation.

  const _exOther = (a.q14_exercise_type_other || '').toLowerCase();
  if (_exOther) {
    a.q14_exercise_type = Array.isArray(a.q14_exercise_type) ? [...a.q14_exercise_type] : [];
    if (/row|kayak|canoe|swim|cycl|bike|tri|run|marathon|cross.?fit|hiit|boot.?camp/.test(_exOther))
      a.q14_exercise_type.push('Endurance sports (running, cycling, swimming)');
    if (/martial|box|wrestl|judo|bjj|karate|taekwondo/.test(_exOther))
      a.q14_exercise_type.push('Team or competitive sports');
    if (/danc|barre|aerial|gymnast/.test(_exOther))
      a.q14_exercise_type.push('Yoga, Pilates, or flexibility work');
    if (/lift|weight|power|strength|resistance/.test(_exOther))
      a.q14_exercise_type.push('Strength training / resistance exercise');
  }

  const _suppOther = (a.q23_current_supps_other || '').toLowerCase();
  if (_suppOther) {
    a.q23_current_supps = Array.isArray(a.q23_current_supps) ? [...a.q23_current_supps] : [];
    if (/omega|fish.?oil|dha|epa/.test(_suppOther)) a.q23_current_supps.push('Omega-3 / fish oil');
    if (/vitamin.?d|d3/.test(_suppOther)) a.q23_current_supps.push('Vitamin D');
    if (/magnesium|mag/.test(_suppOther)) a.q23_current_supps.push('Magnesium');
    if (/b.?12|b.?complex|methylfolate|folate/.test(_suppOther)) a.q23_current_supps.push('B vitamins or B-complex');
    if (/coq10|ubiquinol|coenzyme/.test(_suppOther)) a.q23_current_supps.push('CoQ10');
    if (/probiotic|acidophil|lactobacill/.test(_suppOther)) a.q23_current_supps.push('Probiotic');
    if (/calcium/.test(_suppOther)) a.q23_current_supps.push('Calcium');
    if (/protein|whey|collagen|amino/.test(_suppOther)) a.q23_current_supps.push('Protein supplement or meal replacement');
    if (/statin|lipitor|crestor|rosuvastatin|atorvastatin/.test(_suppOther)) a.q23_current_supps.push('statin');
  }

  const _dietOther = (a.q10_diet_type_other || '').toLowerCase();
  if (_dietOther) {
    a.q10_diet_type = Array.isArray(a.q10_diet_type) ? [...a.q10_diet_type] : [];
    if (/paleo|aip|autoimmune/.test(_dietOther)) a.q10_diet_type.push('Mediterranean or whole foods');
    if (/carnivore|meat.?only/.test(_dietOther)) a.q10_diet_type.push('Low-carb or ketogenic');
    if (/fodmap|ibs|crohn|digest/.test(_dietOther)) a.q10_diet_type.push('Gluten-free');
    if (/vegan|plant.?based/.test(_dietOther)) a.q10_diet_type.push('Plant-based or vegan');
    if (/vegetarian/.test(_dietOther)) a.q10_diet_type.push('Vegetarian');
    if (/keto|low.?carb|atkins/.test(_dietOther)) a.q10_diet_type.push('Low-carb or ketogenic');
    if (/intermittent|fasting|if|16.?8/.test(_dietOther)) a.q10_diet_type.push('Mediterranean or whole foods');
  }

  const _protOther = (a.q7_protein_other || '').toLowerCase();
  if (_protOther) {
    a.q7_protein = Array.isArray(a.q7_protein) ? [...a.q7_protein] : [];
    if (/collagen|bone.?broth|gelatin/.test(_protOther)) a.q7_protein.push('Animal proteins (meat, poultry, fish)');
    if (/whey|casein/.test(_protOther)) a.q7_protein.push('Protein supplements or shakes');
    if (/tofu|tempeh|edamame|seitan|pea|rice.?protein/.test(_protOther)) a.q7_protein.push('Plant proteins (beans, legumes, tofu)');
    if (/fish|seafood|salmon|tuna|shrimp/.test(_protOther)) a.q7_protein.push('Animal proteins (meat, poultry, fish)');
  }

  const _priOther = (a.q19_top_priorities_other || '').toLowerCase();
  if (_priOther) {
    a.q19_top_priorities = Array.isArray(a.q19_top_priorities) ? [...a.q19_top_priorities] : [];
    if (/libido|sexual|intimac|sex.?health|drive/.test(_priOther)) {
      a.q19_top_priorities.push('Managing stress and supporting emotional wellbeing');
      a.q19_top_priorities.push('Maintaining or building energy and vitality');
    }
    if (/longev|aging|longevity|cellular/.test(_priOther)) a.q19_top_priorities.push('Supporting healthy aging and longevity');
    if (/weight|body.?comp|fat|slim/.test(_priOther)) a.q19_top_priorities.push('Weight management and healthy body composition');
    if (/detox|cleanse|liver/.test(_priOther)) a.q19_top_priorities.push('Digestive health and gut function');
    if (/spiritual|purpose|meaning/.test(_priOther)) a.q19_top_priorities.push('Managing stress and supporting emotional wellbeing');
    if (/hormon|thyroid|adrenal|pcos/.test(_priOther)) a.q19_top_priorities.push('Managing stress and supporting emotional wellbeing');
  }

  const _specOther = (a.q22_specific_other || '').toLowerCase();
  if (_specOther) {
    a.q22_specific = Array.isArray(a.q22_specific) ? [...a.q22_specific] : [];
    if (/libido|sexual|intimac|sex.?health|drive/.test(_specOther)) {
      a.q22_specific.push('Mood balance and emotional resilience');
      a.q22_specific.push('Recovery from physical exertion');
    }
    if (/eye|vision|macular|sight/.test(_specOther)) a.q22_specific.push('Eye health and visual longevity');
    if (/liver|detox|cleanse/.test(_specOther)) a.q22_specific.push('Liver and detoxification support');
    if (/skin|wrinkle|collagen|aging.?skin/.test(_specOther)) a.q22_specific.push('Skin elasticity and visible aging');
    if (/hair|nail/.test(_specOther)) a.q22_specific.push('Hair and nail strength');
    if (/focus|concentrat|adhd|brain.?fog/.test(_specOther)) a.q22_specific.push('Mental focus and clarity under pressure');
    if (/mood|anxiety|depress|emotion/.test(_specOther)) a.q22_specific.push('Mood balance and emotional resilience');
    if (/recover|soreness|muscle/.test(_specOther)) a.q22_specific.push('Recovery from physical exertion');
  }
  // ── End free-text normalization ───────────────────────────────────────────
  // ── Demographics ──────────────────────────────────────────────────────────
  const age   = a.q1_age || '';
  const sex   = a.q2_sex || '';
  const life  = a.q3_life_stage || '';

  const isUnder40    = age === 'Under 40';
  const is4054       = age === '40–54';
  const is50plus     = ['50–64', '65–74', '75 or older'].includes(age);
  const is65plus     = ['65–74', '75 or older'].includes(age);
  const isFemale     = sex === 'Female';
  const isMale       = sex === 'Male';
  const isPregnant   = life.includes('Pregnant') || life.includes('Postpartum');
  const isPeri       = life.includes('Perimenopaus') ||
    (Array.isArray(a.q20_hormonal) && a.q20_hormonal.some(h => h.includes('Peri')));
  const isPostMeno   = life.includes('Postmenopaus') || life.includes('Post-menopaus') ||
    (Array.isArray(a.q20_hormonal) && a.q20_hormonal.some(h => h.includes('Post-menopause')));
  const isProstate   = Array.isArray(a.q20_hormonal) &&
    a.q20_hormonal.some(h => h.includes('prostate') || h.includes('urinary'));
  const needsIron    = isFemale && !isPostMeno && !is50plus;

  // ── Budget ────────────────────────────────────────────────────────────────
  const budget       = a.q26_budget || '';
  const budgetMin    = budget.startsWith('Minimal');
  const budgetMod    = budget.startsWith('Moderate');
  const budgetGen    = budget.startsWith('Generous') || budget.startsWith('Comprehensive');

  // ── Safety ───────────────────────────────────────────────────────────────
  const onBloodThinners = (a.q18_blood_thinners || '') === 'Yes';
  const onStatins       = Array.isArray(a.q23_current_supps) &&
    a.q23_current_supps.some(s => s.toLowerCase().includes('statin'));

  // ── Diet ─────────────────────────────────────────────────────────────────
  const dietType  = Array.isArray(a.q10_diet_type) ? a.q10_diet_type : [];
  const isVegan   = dietType.some(d => d.includes('vegan') || d.toLowerCase().includes('plant-based'));
  const isVeg     = dietType.some(d => d.includes('Vegetarian'));
  const fish      = a.q8_fish || '';
  const lowFish   = ['A few times per month', 'Rarely or never'].includes(fish);
  const dairy     = a.q9_dairy || '';
  const noDairy   = dairy.startsWith('No');
  const veg       = a.q6_vegetables || '';
  const lowVeg    = ['1–2', 'Less than 1'].includes(veg);
  const fruit     = a.q6b_fruit || '';
  const lowFruit  = ['Less than 1', 'Rarely or never'].includes(fruit);
  const dietQ     = a.q5_diet_quality || '';
  const poorDiet  = dietQ.startsWith('Poor') || dietQ.startsWith('Mixed');
  const sugar     = a.q12_sugar || '';
  const highSugar = sugar.startsWith('Frequent') || sugar.startsWith('High');
  const upf       = a.q_upf || '';
  const highUPF   = upf.includes('Most days');
  const protein   = Array.isArray(a.q7_protein) ? a.q7_protein : [];
  const lowProtein = protein.some(p => p.includes("not sure")) ||
    protein.length === 0 || protein.every(p => p.includes('Plant'));

  // ── Format / preferences ─────────────────────────────────────────────────
  const format      = Array.isArray(a.q27_format) ? a.q27_format : [];
  const prefersSoy  = format.some(f => f.includes('soy-based') || f.includes('prefer soy'));
  const avoidsSoy   = format.some(f => f.toLowerCase().includes('avoid soy') || f.includes('non-soy'));
  const cantSwallow = format.some(f => f.includes('difficulty'));

  // ── Lifestyle ────────────────────────────────────────────────────────────
  const sun      = a.q11_sun || '';
  const lowSun   = ['Minimal', 'Very little'].some(s => sun.startsWith(s));
  const exercise = a.q13_exercise || '';
  const exType   = Array.isArray(a.q14_exercise_type) ? a.q14_exercise_type : [];
  const strength = a.q_strength || '';
  const noStrength = strength === 'Not currently' || !exType.some(e => e.includes('Strength'));
  const isAthlete  = exercise.startsWith('Very active') || exercise.startsWith('Active');
  const isEndurance = exType.some(e => e.includes('Endurance'));
  const mindbody  = Array.isArray(a.q_mindbody) ? a.q_mindbody : [];
  const nature    = a.q_nature || '';
  const civic     = a.q_civic || '';
  const sport     = a.q_sport || '';

  // ── Sleep / stress / energy ───────────────────────────────────────────────
  const sleep        = a.q15_sleep || '';
  const sleepHrs     = a.q_sleep_hrs || '';
  const sleepIssues  = Array.isArray(a.q_sleep_issues) ? a.q_sleep_issues : [];
  const poorSleep    = sleep.startsWith('Fair') || sleep.startsWith('Poor');
  const shortSleep   = ['Under 5 hours', '5–6 hours', '6–7 hours'].includes(sleepHrs);
  const stress       = a.q16_stress || '';
  const highStress   = stress.startsWith('High') || stress.startsWith('Very high');
  const energy       = a.q17_energy || '';
  const lowEnergy    = energy.startsWith('Often low') || energy.startsWith('Very low');

  // ── Priorities ───────────────────────────────────────────────────────────
  const pri = Array.isArray(a.q19_top_priorities) ? a.q19_top_priorities : [];
  const wantsCognitive   = is50plus || pri.some(p => p.toLowerCase().includes('cogni') || p.toLowerCase().includes('mental sharpness'));
  const wantsCardio      = pri.some(p => p.toLowerCase().includes('cardio') || p.toLowerCase().includes('heart'));
  const wantsBone        = pri.some(p => p.toLowerCase().includes('bone'));
  const wantsWeight      = pri.some(p => p.toLowerCase().includes('weight'));
  const wantsSleep       = poorSleep || pri.some(p => p.toLowerCase().includes('sleep'));
  const wantsStress      = highStress || pri.some(p => p.toLowerCase().includes('stress'));
  const wantsImmune      = pri.some(p => p.toLowerCase().includes('immune'));
  const wantsGut         = pri.some(p => p.toLowerCase().includes('gut') || p.toLowerCase().includes('digest'));
  const wantsJoint       = pri.some(p => p.toLowerCase().includes('joint'));
  const wantsSkin        = pri.some(p => p.toLowerCase().includes('skin') || p.toLowerCase().includes('collagen'));
  const wantsAthletic    = isAthlete || pri.some(p => p.toLowerCase().includes('athletic') || p.toLowerCase().includes('performance'));
  const wantsLongevity   = is50plus || pri.some(p => p.toLowerCase().includes('aging') || p.toLowerCase().includes('longev'));
  const wantsEnergy      = lowEnergy || pri.some(p => p.toLowerCase().includes('energy') || p.toLowerCase().includes('vitality'));

  // ── Specific concerns ────────────────────────────────────────────────────
  const specific = Array.isArray(a.q22_specific) ? a.q22_specific : [];
  const wantsEye    = specific.some(s => s.toLowerCase().includes('eye'));
  const wantsLiver  = specific.some(s => s.toLowerCase().includes('liver'));
  const wantsMood   = specific.some(s => s.toLowerCase().includes('mood'));
  const wantsFocus  = specific.some(s => s.toLowerCase().includes('focus') || s.toLowerCase().includes('clarity'));
  const wantsHair   = specific.some(s => s.toLowerCase().includes('hair'));
  const wantsRecovery = specific.some(s => s.toLowerCase().includes('recovery'));

  // ── Metabolic ────────────────────────────────────────────────────────────
  const metabolic = Array.isArray(a.q21_metabolic) ? a.q21_metabolic : [];
  const wantsCholesterol   = metabolic.some(m => m.toLowerCase().includes('cholesterol'));
  const wantsBloodSugar    = metabolic.some(m => m.toLowerCase().includes('blood sugar'));
  const wantsBloodPressure = metabolic.some(m => m.toLowerCase().includes('blood pressure'));

  // ── Omega-3 need ──────────────────────────────────────────────────────────
  const onStrip    = !budgetMin && isFemale && !is50plus;
  const needsOmega = lowFish || !onStrip;

  return {
    age, sex, life,
    isUnder40, is4054, is50plus, is65plus,
    isFemale, isMale, isPregnant, needsIron,
    // Original names preserved for downstream compatibility
    isPerimenopause: isPeri, isPostmenopause: isPostMeno,
    isPeri, isPostMeno, isProstate,
    budgetMinimal: budgetMin, budgetModerate: budgetMod, budgetGenerous: budgetGen,
    budgetMin, budgetMod, budgetGen,
    onBloodThinners, onStatins,
    isVegan, isVegetarian: isVeg, isVeg,
    lowFish, limitsDairy: noDairy, noDairy, lowVeg, lowFruit, poorDiet,
    highSugar, highUPF, lowProtein, 
    prefersSoy, avoidsSOY: avoidsSoy, avoidsSoy,
    cannotSwallow: cantSwallow, cantSwallow,
    lowSun, exercise, isAthlete, isEndurance, noStrength,
    mindbody, nature, civic, sport,
    poorSleep, shortSleep, sleepIssues, highStress, lowEnergy,
    pri, specific, metabolic,
    wantsCognitive, wantsCardio, wantsBone, wantsWeight,
    wantsSleep, wantsStress, wantsImmune, wantsGut, wantsJoint,
    wantsSkin, wantsAthletic, wantsLongevity, wantsEnergy,
    wantsEye, wantsLiver, wantsMood, wantsFocus, wantsHair, wantsRecovery,
    wantsCholesterol, wantsBloodSugar, wantsBloodPressure,
    needsOmega, onStrip,
    dietQ, protein, fruit,
  };
}

function buildFoundationalStack(p) {
  const foundational  = [];
  const stripIncludes = new Set();
  let onStrip         = false;
  let multiId         = null;

  // ── MULTIVITAMIN ── (first-match priority, per decisionBranches.json) ────
  if (p.onBloodThinners && p.is50plus && p.budgetGenerous) {
    multiId = 'vitalizer-gold-nok'; onStrip = true;
  } else if (p.onBloodThinners && p.is50plus) {
    multiId = 'vita-lea-gold-nok';
  } else if (p.is50plus && !p.onBloodThinners && p.budgetGenerous) {
    // Life Strip for all comprehensive users — Vivix value outweighs Vita-Lea Gold 50+ optimization.
    // Men and postmenopausal women: no iron. Premenopausal women: with iron (edge case — 50+ premenopause).
    multiId = (p.isMale || p.isPostmenopause) ? 'life-strip-no-iron' : 'life-strip-iron'; onStrip = true;
  } else if (p.is50plus && !p.onBloodThinners) {
    multiId = 'vita-lea-gold-k';
  } else if (p.isFemale && p.isPostmenopause && p.budgetGenerous) {
    multiId = 'life-strip-no-iron'; onStrip = true;
  } else if (p.isFemale && p.budgetGenerous) {
    multiId = 'life-strip-iron'; onStrip = true;
  } else if (p.isMale && p.budgetGenerous) {
    multiId = 'life-strip-no-iron'; onStrip = true;
  } else if (!p.is50plus && p.isFemale && p.budgetModerate) {
    multiId = 'vitalizer-women'; onStrip = true;
  } else if (!p.is50plus && p.isMale && p.budgetModerate) {
    multiId = 'vitalizer-men'; onStrip = true;
  } else if (!p.is50plus && p.isFemale) {
    multiId = 'vita-lea-womens';
  } else {
    // Default: men's multi (also handles non-binary / unspecified)
    multiId = 'vita-lea-mens';
  }

  // ── Track strip inclusions (Patch 2C: branched by strip family) ────
  // Each branch reflects verified label composition for that strip family.
  // See docs/AUDIT_LOG.md "Strip composition fabrication" entry for the
  // full suppression matrix and label verification dates.
  //
  // Vitalizer Men / Vitalizer Women (under 50):
  //   Caroto-E-Omega delivers omega-3 + carotenoids + vitamin E
  //     → suppress omegaguard, carotomax
  //   B+C Complex → suppress b-complex
  //   Optiflora Pearl Probiotic (functional equivalent of optiflora-di)
  //     → suppress optiflora-di
  //   CoQ10 NOT in supplement facts panel for under-50 variants
  //     → do NOT suppress coqheart
  if (multiId === 'vitalizer-men' || multiId === 'vitalizer-women') {
    ['omegaguard', 'carotomax', 'b-complex', 'optiflora-di']
      .forEach(id => stripIncludes.add(id));
  }

  // Vitalizer Gold-K / Vitalizer Gold-NoK (50+):
  //   Same as under-50 PLUS CoQ10 30mg explicitly in supplement facts
  //     → also suppress coqheart
  if (multiId === 'vitalizer-gold-k' || multiId === 'vitalizer-gold-nok') {
    ['omegaguard', 'carotomax', 'coqheart', 'b-complex', 'optiflora-di']
      .forEach(id => stripIncludes.add(id));
  }

  // Life Strip (both variants):
  //   OmegaGuard Plus → suppress omegaguard
  //   Carotenoids (Lutein, Zeaxanthin, Lycopene) in panel
  //     → suppress carotomax
  //   CoQ10 30mg in panel → suppress coqheart
  //   B+C Complex → suppress b-complex
  //   Vivix Liquid Gels → suppress vivix-original
  //   NO probiotic in Life Strip → do NOT suppress optiflora-di
  //     (Life Strip users with gut health needs must be offered probiotic)
  if (multiId === 'life-strip-iron' || multiId === 'life-strip-no-iron') {
    ['omegaguard', 'carotomax', 'coqheart', 'b-complex', 'vivix-original']
      .forEach(id => stripIncludes.add(id));
  }

  // Iron tracking — Life Strip with iron only
  if (multiId === 'life-strip-iron') {
    stripIncludes.add('iron');
  }

  // Build multi why text
  const multiWhy = buildMultiWhy(multiId, p);
  const multiCard = card(multiId, { tier: 1, why: multiWhy });
  if (multiCard) {
    foundational.push(multiCard);
  }

  // ── OMEGA-3 ─────────────────────────────────────────────────────────────
  // Skip if on strip — UNLESS lowFish (override: strip dose is insufficient for low-fish diet)
  const needsOmega = !onStrip || p.lowFish;
  if (needsOmega) {
    const omegaId   = p.isVegan ? 'vegan-omega-3' : 'omegaguard';
    const omegaNote = p.onBloodThinners
      ? 'You indicated blood-thinning medication use. Discuss omega-3 supplementation with your physician before adding — omega-3 has anticoagulant properties.'
      : null;
    const omegaWhy  = p.isVegan
      ? 'You follow a plant-based diet — algae-derived DHA+EPA is the only non-animal omega-3 source and delivers identical cardiovascular and cognitive benefits to fish oil. Algae is where fish get their omega-3 in the first place.'
      : p.lowFish
        ? 'Your fish intake is low, making supplemental omega-3 particularly important. Even on the Vitalizer strip, the omega-3 dose is calibrated for average dietary intake — low-fish consumers need additional supplementation to reach clinically meaningful levels.'
        : 'Full-spectrum sustainably sourced fish oil — 1,200mg per 2-softgel serving providing 667mg total omega-3 (EPA 363mg + DHA 240mg + 64mg of five other naturally found omega-3 fatty acids). EPA+DHA at clinically meaningful doses for cardiovascular, cognitive, joint, and anti-inflammatory support.';
    const omegaCard = card(omegaId, { tier: 1, why: omegaWhy, note: omegaNote });
    if (omegaCard) foundational.push(omegaCard);
  }

  // ── VITAMIN D ────────────────────────────────────────────────────────────
  // Activates on: (lowSun OR is50plus) AND NOT budgetGenerous (strips include D)
  if ((p.lowSun || p.is50plus) && !p.budgetGenerous) {
    const dWhy = (p.is50plus && p.lowSun)
      ? 'Both your age and sun exposure pattern independently indicate vitamin D supplementation. After 50, skin synthesis efficiency drops and absorption decreases — supplementation becomes necessary regardless of outdoor time.'
      : p.is50plus
        ? 'Adults 50+ have significantly reduced skin synthesis of vitamin D. 2,000 IU D3 is appropriate maintenance-level supplementation for most adults in this age range.'
        : 'Your sun exposure pattern suggests insufficient vitamin D synthesis. This is among the most commonly needed and most easily corrected nutritional gaps.';
    const dCard = card('vita-d3', { tier: 1, why: dWhy });
    if (dCard) foundational.push(dCard);
  }

  // ── PROTEIN ──────────────────────────────────────────────────────────────────
  // Foundation protein = LifeShake always (adequacy baseline for everyone).
  // Exception: minimal budget users ALWAYS get Meal Shakes — unconditional,
  // regardless of whether protein branch flags fire.
  // Advanced Physique Whey (Build) and Build+ are TARGETED products only.
  if (p.budgetMin) {
    const mealShakesCard = card('meal-shakes', {
      tier: 1,
      why: 'A complete protein shake at an accessible price point — the entry product that built this business. At Member pricing, one container (16 servings) is roughly $30. Daily use alongside Vita-Lea gives you the foundational protein and micronutrient base that produces noticeable results for most people starting from scratch. When budget allows, LifeShake is the upgraded version with a more complete nutritional panel.',
    });
    if (mealShakesCard) foundational.push(mealShakesCard);
  } else if (p.isAthlete || p.wantsWeight || p.isVegan || p.lowProtein) {
    // Non-minimal budget: LifeShake protein branch
    {
      const proteinId = (p.isVegan || p.isVegetarian)
        ? 'lifeshake-plant'
        : p.prefersSoy
          ? 'lifeshake-soy'
          : 'lifeshake-plant';

      const proteinWhy = (p.isVegan || p.isVegetarian)
        ? 'Complete plant protein — pea + rice combination provides all essential amino acids and meets the leucine threshold (2.5–3g) for muscle protein synthesis. Plant-based diets frequently run low on leucine specifically, which is the primary trigger for muscle building and repair.'
        : p.prefersSoy
          ? 'LifeShake Soy delivers 20g leucine-powered soy protein with 6g fiber, a prebiotic blend, digestive enzymes, ALA omega-3, and 25 vitamins and minerals. Non-GMO, vegan, gluten-free, and kosher — your daily protein and micronutrient foundation in one.'
          : p.isAthlete
            ? 'LifeShake is your daily protein foundation — 20g complete protein with added leucine and a full micronutrient panel. For athletes, this covers your baseline adequacy at every meal. Build (Advanced Physique Whey) appears in your Athletic module for post-training recovery, where the leucine concentration and timing window specifically drive adaptation.'
            : p.wantsWeight
              ? 'Protein is the single most important dietary variable for body composition management. LifeShake provides 20g complete protein with a full micronutrient panel — preserves lean mass during caloric restriction and drives satiety more effectively than any other macronutrient.'
              : 'Your protein intake appears insufficient for optimal health. LifeShake provides complete protein with a full micronutrient panel — versatile enough to function as meal replacement or supplement depending on your daily needs.';

      const proteinCard = card(proteinId, { tier: 1, why: proteinWhy });
      if (proteinCard) foundational.push(proteinCard);
    }
  }

  // ── PROBIOTIC (if not on strip) ──────────────────────────────────────────
  if (!onStrip) {
    const probCard = card('optiflora-di', {
      tier: 1,
      why: 'A quality multi-strain probiotic is foundational — gut microbiome health connects to immune function, mood, nutrient absorption, and inflammation. 4 clinically validated strains at 10 billion CFU is a meaningful therapeutic dose, not a token gesture.',
    });
    if (probCard) foundational.push(probCard);
  }

  // ── IRON (premenopausal women, not already in strip) ─────────────────────
  if (p.needsIron && !stripIncludes.has('iron') && multiId !== 'life-strip-iron') {
    const ironCard = card('iron-plus-c', {
      tier: 1,
      why: 'Iron depletion is the most common nutritional deficiency in premenopausal women and among the most commonly missed causes of fatigue and cognitive fog. This formula pairs iron with Vitamin C for 3–4x greater absorption.',
      note: 'If you have not had iron levels checked recently, it\'s worth doing — excess iron is also problematic. This is appropriate for most premenopausal women without known iron overload.',
    });
    if (ironCard) foundational.push(ironCard);
  }

  return { foundational, stripIncludes, onStrip };
}

// Multi why text — specific to each selection
function buildMultiWhy(id, p) {
  switch (id) {
    case 'vitalizer-gold-nok':
      return 'Vitalizer Gold (no Vitamin K) is the most comprehensive foundational system for adults 50+ on blood-thinning medications. The no-K formulation is safe for your situation while delivering the full Vitalizer Gold strip: omega-3, CoQ10, carotenoids, and probiotic alongside the 50+ multi.';
    case 'vita-lea-gold-nok':
      return 'Vita-Lea Gold without Vitamin K — formulated for adults 50+ on blood thinners. Elevated B12, magnesium, and zinc address the specific nutrient needs that shift after 50, without the K that interacts with anticoagulant medications.';
    case 'vitalizer-gold-k':
      return 'Vitalizer Gold is the most comprehensive strip system for adults 50+ at a moderate investment — omega-3, CoQ10, carotenoids, and probiotic alongside the Vita-Lea Gold 50+ multi. For comprehensive investment, Life Strip with Vivix delivers even greater value.';
    case 'vita-lea-gold-k':
      return 'Vita-Lea Gold is formulated specifically for adults 50+ — elevated B12 (absorption decreases significantly with age), magnesium, zinc, and D3 address the ways nutritional needs measurably shift after 50.';
    case 'life-strip-iron':
      return 'Life Strip (with iron) is the most comprehensive daily foundation for premenopausal women — one strip delivers Vivix polyphenol concentrate, omega-3, CoQ10, carotenoids, probiotic, B+C complex, and iron. Your entire foundational nutritional base, accounted for.';
    case 'life-strip-no-iron':
      return p.isPostmenopause
        ? 'Life Strip (no iron) is the most comprehensive daily foundation for postmenopausal women — one strip delivers Vivix polyphenol concentrate, omega-3, CoQ10, carotenoids, probiotic, and B+C complex. Iron appropriately excluded post-menopause.'
        : 'Life Strip — the most comprehensive daily foundation for men under 50. One strip delivers Vivix polyphenol concentrate, omega-3, CoQ10, carotenoids, probiotic, and B+C complex.';
    case 'vitalizer-women':
      return 'Vitalizer for Women — strip system including omega-3, CoQ10, carotenoids, and probiotic alongside the women\'s multi. The most complete daily foundation at a moderate investment.';
    case 'vitalizer-men':
      return 'Vitalizer for Men — strip system including omega-3, CoQ10, carotenoids, and probiotic alongside the men\'s multi. The most complete daily foundation at a moderate investment.';
    case 'vita-lea-womens':
      return 'Vita-Lea Women\'s Multi — 23 essential nutrients calibrated for women\'s needs, including iron for premenopausal support and calcium at levels appropriate for bone health.';
    case 'vita-lea-mens':
    default:
      return 'Vita-Lea Men\'s Multi — 28 essential nutrients including lycopene for prostate support, iron-free (appropriate for men), and a full B-complex panel for energy and nervous system function.';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PART 3 — MODULE ACTIVATION
// 13 targeted modules. Each has explicit activation conditions from
// decisionBranches.json. Products selected based on profile flags.
// Returns modules[] in Results.jsx schema: { id, title, icon, rationale, products[] }
// ─────────────────────────────────────────────────────────────────────────────

function activateModules(p, stripIncludes, onStrip) {
  const modules   = [];
  // vivixAdded: true if Vivix is already included via the strip (Life-Strip includes it, Vitalizer strips do not)
  let vivixAdded = stripIncludes.has('vivix-original');

  // ── MODULE 1: COGNITIVE ────────────────────────────────────────────────
  const cogActivated = p.wantsCognitive || p.is50plus || p.wantsMood
    || (p.raw?.energy || '').toLowerCase().includes('mental focus');

  if (cogActivated) {
    const cogProducts = [];

    cogProducts.push(card('mindworks', {
      tier: 2,
      why: p.is50plus
        ? 'Age 50+ activates this module — B12 absorption decreases measurably after 50, and cognitive function shows early age-related changes. MindWorks pairs a high-potency B12 dose (20,833% DV) with the MindWorks Smart proprietary blend (Chardonnay grape, guarana, blueberry, green coffee) that the label states tested at 3x better short-term memory responses versus control at six hours.'
        : p.wantsMood
          ? 'You flagged mood alongside cognitive function — the overlap is real. The high-potency B-vitamin foundation in MindWorks (B6 at 1,176% DV, folate at 334% DV, B12 at 20,833% DV) supports neurotransmitter synthesis pathways that affect both cognition and mood through the same biochemical channels.'
          : 'You identified cognitive performance as a priority. MindWorks is the most targeted Shaklee cognitive product — the MindWorks Smart proprietary blend (Chardonnay grape, guarana, blueberry, green coffee) paired with high-potency B-vitamin foundation. Label states 3x better short-term memory responses vs. control at six hours.',
      note: 'Not intended for use by children, or women who are pregnant or breastfeeding. If taking medications, please discuss use with your physician.',
    }));

    // Add Vivix for 50+ if not already in strip
    if (p.is50plus && !vivixAdded) {
      const vivixId = p.prefersGummy ? 'vivix-gummy' : 'vivix-original';
      cogProducts.push(card(vivixId, {
        tier: 2,
        why: 'At 50+, accumulated oxidative stress and inflammatory burden are where Vivix does its most meaningful work. The Ghanim 2011 RCT specifically measured suppression of post-meal inflammatory gene expression — the kind of chronic low-grade inflammation that correlates with cognitive aging.',
        note: vivixId === 'vivix-original' ? 'Requires refrigeration after opening. Highest polyphenol dose of all Vivix formats.' : null,
      }));
      vivixAdded = true;
    }

    modules.push({
      id: 'cognitive',
      title: 'Cognitive Function & Brain Health',
      icon: '🧠',
      rationale: p.is50plus
        ? 'Cognitive support activates at 50+ — B12 absorption declines, homocysteine tends to rise, and the accumulation of oxidative stress begins to affect neurological function. These products address the most correctable nutritional contributors to that trajectory.'
        : p.wantsMood
          ? 'You flagged both mood and cognitive performance. The nutritional drivers overlap significantly — neurotransmitter synthesis, B vitamin status, and anti-inflammatory support affect both. These products address the common pathways.'
          : 'You identified cognitive performance as a priority. These products address the most nutritionally correctable contributors to focus, memory, and mental clarity.',
      products: cogProducts,
    });
  }

  // ── MODULE 2: SLEEP ───────────────────────────────────────────────────
  if (p.poorSleep || p.shortSleep || p.wantsSleep) {
    const sleepProducts = [];

    sleepProducts.push(card('dream-serene', {
      tier: 2,
      why: p.poorSleep
        ? 'Your sleep quality assessment triggered this recommendation. Melatonin at 2mg (a restorative dose, not a pharmacological dose) combined with valerian root and L-theanine addresses sleep onset without next-morning grogginess. The L-theanine specifically promotes alpha-wave activity that facilitates the transition to sleep.'
        : 'Melatonin 2mg + valerian root + lemon balm + L-theanine — designed for sleep onset support, particularly for disrupted or variable sleep schedules.',
      note: 'Do not drive or operate machinery after taking. Best taken 30–45 minutes before target sleep time.',
    }));

    if (p.highStress) {
      sleepProducts.push(card('rest-rewind', {
        tier: 2,
        why: 'Your elevated stress level is likely a primary contributor to your sleep difficulty. Rest & Rewind addresses the stress-to-sleep conversion pathway specifically: 5-HTP supports the serotonin → melatonin conversion chain, while magnesium glycinate at a therapeutic dose supports GABA receptor function — the neurological brake that chronic cortisol systematically suppresses.',
        note: 'Contains 5-HTP — do not combine with SSRIs, MAOIs, or other serotonergic medications without physician guidance.',
      }));
    }

    modules.push({
      id: 'sleep',
      title: 'Sleep Quality & Restoration',
      icon: '🌙',
      rationale: (p.highStress && p.poorSleep)
        ? 'Your assessment shows both elevated stress and compromised sleep — two conditions that reinforce each other. This module addresses the stress-to-sleep pathway and the direct mechanisms of sleep onset and architecture together.'
        : 'Sleep is when the body consolidates memory, repairs tissue, regulates hormones, and clears metabolic waste from the brain. These products support both sleep onset and sleep quality.',
      products: sleepProducts,
    });
  }

  // ── MODULE 3: STRESS ──────────────────────────────────────────────────
  if (p.highStress || p.wantsStress) {
    const stressProducts = [];

    stressProducts.push(p.wantsMood
      ? card('cheer-up-chill-out', {
          tier: 2,
          why: 'You flagged both stress and mood. Cheer Up Chill Out combines saffron extract (clinical evidence for mood support, Hausenblas et al. 2013 meta-analysis) with ashwagandha (cortisol modulation) plus B6 and B12 at 1,000% DV — addressing both mood and stress pathways simultaneously rather than picking one.',
        })
      : card('stress-relief-complex', {
          tier: 2,
          why: 'Ashwagandha root extract has Grade A clinical evidence for cortisol reduction. Chandrasekhar et al. 2012 (Ind J Psychol Med) showed a 27.9% reduction in serum cortisol versus placebo — the primary cited RCT in the science library. Paired here with L-theanine and L-tyrosine. This is not herbal wellness marketing — the evidence quality is high.',
        })
    );

    // B-Complex alongside adaptogens (unless on Life-Strip, which includes it)
    if (!stripIncludes.has('b-complex')) {
      stressProducts.push(card('b-complex', {
        tier: 2,
        why: 'B vitamins are depleted under sustained stress — they\'re cofactors in every step of neurotransmitter synthesis. Low B vitamin status directly impairs the serotonin and GABA production that the adaptogen is trying to support. This provides the neurological foundation.',
      }));
    }

    modules.push({
      id: 'stress',
      title: 'Stress Response & Nervous System Support',
      icon: '⚡',
      rationale: 'Elevated stress has downstream effects on sleep, immune function, body composition, cognitive performance, and cardiovascular health — it\'s worth addressing at the physiological level rather than managing symptomatically. These products address cortisol, neurotransmitter support, and mood through evidence-based mechanisms.',
      products: stressProducts,
    });
  }

  // ── MODULE 4: IMMUNE ──────────────────────────────────────────────────
  if (p.wantsImmune || p.is65plus) {
    const immuneProducts = [];

    immuneProducts.push(card('triple-defense-boost', {
      tier: 2,
      why: p.is65plus
        ? 'Immune function declines measurably with age — a process called immunosenescence. The D3/C/Zinc combination addresses the three most evidence-supported nutritional drivers of immune competence, particularly for older adults where D3 insufficiency is near-universal.'
        : 'Vitamin D3 (2,000 IU), Vitamin C (500mg), and Zinc in combination represent the most evidence-dense immune support stack available. Cochrane reviews confirm significant reductions in illness duration and severity.',
    }));

    if (!p.budgetMinimal) {
      immuneProducts.push(card('nutriferon', {
        tier: 2,
        why: 'NutriFeron works through a different mechanism than standard D/C/Zinc — it activates the body\'s natural interferon pathway via the MACH botanical blend, formulated by Dr. Yasuhiko Kojima after screening 200+ herbs. A controlled trial demonstrated statistically significant increases in gamma interferon levels versus placebo. It\'s an additive mechanism that standard supplementation doesn\'t replicate.',
      }));
    }

    modules.push({
      id: 'immune',
      title: 'Immune System Support',
      icon: '🛡',
      rationale: p.is65plus
        ? 'Immune function declines predictably and measurably with age. At 65+, proactive nutritional immune support is standard evidence-based practice, not optional enhancement. These products address the most important nutritional contributors to immune competence.'
        : 'You identified immune health as a priority. These recommendations address the most nutritionally correctable contributors to immune resilience, from two distinct mechanisms.',
      products: immuneProducts,
    });
  }

  // ── MODULE 5: CARDIOVASCULAR ──────────────────────────────────────────
  const cardioActivated = p.wantsCardio || p.is50plus || p.cholesterol || p.bloodPressure;
  if (cardioActivated) {
    const cardioProducts = [];

    if (p.cholesterol) {
      cardioProducts.push(card('cholesterol-reduction', {
        tier: 2,
        why: 'You indicated cholesterol management as a concern. Plant sterols are among the most evidence-supported natural LDL reducers — they block cholesterol absorption at the intestinal level. FDA-authorized health claim. Clinical trials consistently show 10–15% LDL reduction at 2g/day.',
        note: 'Not a replacement for medical evaluation or statin therapy where medically indicated. Discuss with your physician.',
      }));
    }

    if (p.bloodPressure) {
      cardioProducts.push(card('blood-pressure', {
        tier: 2,
        why: 'You indicated blood pressure management as a concern. Blood Pressure Support Blend combines potassium nitrate (a nitric oxide precursor supporting vasodilation), quercetin from Sophora japonica (a bioflavonoid with endothelial effects), beet root, grape seed extract, and spinach. Vitamin C and Magnesium round out the formula. This is nutritional support — not a replacement for prescribed antihypertensive therapy.',
        note: '⚠ Important: Consult your physician before use if you have hypertension, cardiovascular disease, or ED, or if you are taking any blood pressure medications, ACE inhibitors, ARBs, diuretics, nitrates, or ED medications. The vasodilatory and potassium content can interact with these medications. Do not discontinue any medications without physician guidance. Not for children or pregnant/breastfeeding women.',
      }));
    }

    // CoQ10 — add if not in strip OR if on statins (statin depletion override)
    if (!stripIncludes.has('coqheart') || p.onStatins) {
      cardioProducts.push(card('coqheart', {
        tier: 2,
        why: p.onStatins
          ? 'Statins significantly deplete CoQ10 — a well-documented and clinically relevant drug-nutrient interaction. CoQ10 supplementation is strongly indicated for anyone on statin therapy. This addresses a direct medication-induced nutrient depletion that affects both energy and cardiovascular function.'
          : p.is50plus
            ? 'CoQ10 synthesis declines with age, accelerating after 50. As the primary mitochondrial energy carrier, CoQ10 affects both cardiovascular function and general cellular energy production. Supplementation becomes increasingly necessary as synthesis falls.'
            : 'CoQ10 supports mitochondrial energy production and cardiovascular function. Grade A evidence for heart failure; strong Grade B for general cardiovascular health support.',
      }));
    }

    if (cardioProducts.length > 0) {
      modules.push({
        id: 'cardiovascular',
        title: 'Cardiovascular Health',
        icon: '❤️',
        rationale: p.cholesterol
          ? 'You indicated cholesterol management as a concern. This module targets both the lipid dimension (LDL reduction via plant sterols) and the vascular/energy dimension (CoQ10, bioflavonoids) of cardiovascular health.'
          : p.bloodPressure
            ? 'Blood pressure is among the most directly measurable cardiovascular risk factors. This module addresses blood pressure regulation, vascular integrity, and mitochondrial cardiovascular function.'
            : 'Cardiovascular health activates at 50+ as a proactive priority. The mitochondrial and vascular support here addresses the most nutritionally relevant contributors to long-term cardiovascular resilience.',
        products: cardioProducts,
      });
    }
  }

  // ── MODULE 6: GUT HEALTH ─────────────────────────────────────────────
  if (p.wantsGut) {
    const gutProducts = [];

    gutProducts.push(card('enzyme-complex', {
      tier: 2,
      why: 'Digestive enzymes address the root cause of many digestive complaints — insufficient enzymatic capacity for complete macronutrient breakdown. Protein, fat, and carbohydrate digestion are all supported, reducing fermentation, bloating, and discomfort from incomplete digestion.',
    }));

    gutProducts.push(card('stomach-soothing-complex', {
      tier: 2,
      why: 'Ginger, licorice root extract, and papain address digestive discomfort and support the gut lining. A good fit when symptoms include nausea, reflux, or post-meal discomfort.',
    }));

    if (!onStrip) {
      // Probiotic for gut health module if not already in foundational
      gutProducts.push(card('flora-flow', {
        tier: 2,
        why: 'A women-specific probiotic blend — 5 strains including Lactobacillus crispatus and Lactobacillus rhamnosus — targeting both gut and vaginal microbiome health. If this isn\'t relevant, Optiflora DI in your foundation covers gut microbiome support.',
      }));
    }

    modules.push({
      id: 'gut',
      title: 'Digestive Health & Gut Microbiome',
      icon: '🦠',
      rationale: 'You identified digestive health as a priority. Gut health connects directly to immune function, mood, nutrient absorption, and systemic inflammation — supporting it addresses multiple health domains simultaneously. These products target enzymatic capacity, gut lining, and microbial balance.',
      products: gutProducts,
    });
  }

  // ── MODULE 7: BONE HEALTH ────────────────────────────────────────────
  // ── LIFE+ TIER COMPUTATION ────────────────────────────────────────────
  const lifePlusGoals = [p.wantsJoint, p.wantsSkin, p.wantsHair, p.wantsBone, p.wantsLongevity, p.isAthlete].filter(Boolean).length;
  const lifePlusTier = !p.budgetMin ? (lifePlusGoals >= 2 ? 1 : 2) : null;

  if (p.wantsBone || p.isPostmenopause || p.limitsDairy) {
    modules.push({
      id: 'bone',
      title: 'Bone Density & Skeletal Health',
      icon: '🦴',
      rationale: p.isPostmenopause
        ? 'Bone density loss accelerates significantly following menopause — estrogen withdrawal removes its bone-protective effect. This is the highest-return window for nutritional intervention. OsteoMatrix addresses all four pillars of bone density support.'
        : p.limitsDairy
          ? 'Limited dairy means your calcium intake is likely below optimal. OsteoMatrix provides the calcium/magnesium/D3/K2 combination that dairy would otherwise supply, in well-absorbed chelated form.'
          : 'Bone health was identified as a priority. Proactive bone nutrition maintains the structural reserve that becomes critically important in later decades.',
      products: (() => {
        const bp = [card('osteomatrix', {
          tier: 2,
          why: p.isPostmenopause
            ? 'The calcium/magnesium/D3/K2 matrix addresses all four nutritional pillars of bone density support. K2 — the often-missing piece — is required to direct calcium into bone rather than allowing it to deposit in soft tissue. This is the current evidence standard for bone mineral density support post-menopause.'
            : 'OsteoMatrix delivers calcium, magnesium, D3, and K2 in combination — the complete nutritional support system for bone density. The K2 component is what distinguishes this from basic calcium supplementation.',
        })];
        if (lifePlusTier !== null) {
          bp.push(card('life-plus', {
            tier: lifePlusTier,
            why: 'Life+ supports the collagen matrix dimension of bone health — the structural protein scaffold that mineralization builds on. Bioactive collagen peptides support bone collagen integrity and renewal, while calcium fructoborate provides targeted bone density support that complements OsteoMatrix mineralization role.',
            note: 'Poultry-derived (hydrolyzed chicken sternal cartilage) — not appropriate for vegetarians or vegans. Not recommended during pregnancy or nursing.',
          }));
        }
        return bp;
      })(),
    });
  }

  // ── MODULE 8: JOINT HEALTH ──────────────────────────────────────────
  if (p.wantsJoint || p.isAthlete) {
    const jointNotes = [];
    if (p.onBloodThinners) {
      jointNotes.push('Boswellia may have mild anticoagulant properties — consult your physician given your blood-thinner use.');
    }

    const jointProducts = [
      card('joint-health-complex', {
        tier: lifePlusTier !== null ? 3 : 2,
        why: 'Glucosamine HCl 1,500mg + patented Boswellia AKBA Standardized Extract — structural substrate for cartilage matrix (Cochrane review on glucosamine) paired with a fast-acting boswellia extract shown in clinical study to improve joint comfort. Two mechanisms: structural building blocks and inflammatory modulation.',
        note: jointNotes.join(' '),
      }),
    ];
    if (lifePlusTier !== null) {
      jointProducts.push(card('life-plus', {
        tier: lifePlusTier,
        why: p.isAthlete
          ? 'For athletes, Life+ adds the collagen matrix dimension to your joint protocol — bioactive collagen peptides for cartilage integrity, hyaluronic acid for synovial fluid support, and calcium fructoborate for bone density. TriPlex Technology addresses joint, connective tissue, and structural aging simultaneously, complementing the glucosamine and boswellia foundation.'
          : 'Life+ adds the collagen lifecycle dimension to your joint protocol — where glucosamine provides structural building blocks and boswellia modulates inflammatory signals, Life+ works across collagen protection, renewal, and the hydrated environment cartilage depends on. The Collagen/HA Matrix is clinically studied for joint mobility and connective tissue integrity.',
        note: 'Poultry-derived (hydrolyzed chicken sternal cartilage) — not appropriate for vegetarians or vegans. Not recommended during pregnancy or nursing.',
      }));
    }


    modules.push({
      id: 'joint',
      title: 'Joint Comfort & Mobility',
      icon: '🏃',
      rationale: p.isAthlete
        ? 'Athletic activity generates cumulative joint wear. Proactive joint nutrition supports recovery and long-term mobility — these are structural and anti-inflammatory components, not pain maskers.'
        : 'You identified joint health as a priority. Glucosamine has decades of clinical evidence for cartilage support, and patented Boswellia AKBA adds a fast-acting joint comfort dimension validated in clinical study.',
      products: jointProducts,
    });
  }

  // ── MODULE 9: ATHLETIC PERFORMANCE ──────────────────────────────────
  if (p.isAthlete || p.wantsAthletic) {
    const athProducts = [];

    athProducts.push(card('build-plus', {
      tier: 2,
      why: 'Recovery is where adaptation happens — it\'s the recovery from training, not the training itself, that produces the fitness gains. Build+ delivers the intelligent-release protein + carbohydrate combination engineered for the 30–60 minute post-workout window, when muscle protein synthesis is maximally responsive to leucine-rich protein paired with glycogen restoration.',
    }));

    athProducts.push(card('electrolyte-plus', {
      tier: 2,
      why: 'Most athletes underestimate electrolyte replacement — particularly sodium and magnesium. Electrolyte imbalance is the primary driver of performance-limiting dehydration and muscle cramping, not just water deficit.',
    }));


    modules.push({
      id: 'athletic',
      title: 'Athletic Performance & Recovery',
      icon: '💪',
      rationale: 'Your training level activates this module. Performance nutrition extends well beyond protein — it includes recovery optimization, electrolyte management, and the micronutrient support that underlies adaptation. What you do in the 60 minutes after training matters as much as the training itself.',
      products: athProducts,
    });
  }

  // ── MODULE 10: MENOPAUSE / HORMONAL TRANSITION ───────────────────────
  if (p.isPerimenopause || p.isPostmenopause) {
    modules.push({
      id: 'menopause',
      title: 'Hormonal Transition Support',
      icon: '🌸',
      rationale: p.isPerimenopause
        ? 'Perimenopause is a multi-year hormonal transition with variable and often unpredictable symptoms. These products address the most evidence-supported nutritional aspects of that transition — vasomotor symptoms, connective tissue changes, and the inflammatory dimension of hormonal shift.'
        : 'Post-menopause nutritional support addresses the ongoing hormonal environment, vasomotor symptoms, and the connective tissue and inflammatory changes that persist beyond the acute transition.',
      products: [
        card('menopause-balance', {
          tier: 2,
          why: p.isPerimenopause
            ? 'Black cohosh has Grade B evidence for vasomotor symptoms (hot flashes) across multiple controlled trials. The combination formula addresses the multi-system nature of perimenopause more comprehensively than single-herb approaches.'
            : 'Ongoing support for the post-menopausal hormonal environment — black cohosh for vasomotor symptoms, soy isoflavones as selective estrogen receptor modulators.',
          note: 'Black cohosh is not appropriate with hormone-sensitive cancer history (e.g. ER+ breast cancer) or with hormone replacement therapy. Physician consultation recommended.',
        }),
        card('gla-complex', {
          tier: 2,
          why: 'GLA from evening primrose oil specifically addresses the skin dryness, inflammatory discomfort, and connective tissue changes associated with hormonal transition — a dimension that black cohosh doesn\'t cover. For many people, it\'s the most immediately noticeable difference.',
        }),
      ],
    });
  }

  // ── MODULE 11: MEN'S HEALTH ──────────────────────────────────────────
  if (p.menUrinary || (p.isMale && p.is50plus)) {
    modules.push({
      id: 'mens_health',
      title: "Men's Health & Prostate Support",
      icon: '🔵',
      rationale: p.menUrinary
        ? 'You indicated urinary health as a specific concern. Saw palmetto has Cochrane-reviewed evidence for significant improvement in urinary symptoms in benign prostatic hyperplasia — the most studied botanical for this application.'
        : 'Prostate health support activates at 50+ for men — proactive rather than reactive. The evidence base for saw palmetto is stronger than most botanical applications.',
      products: [card('saw-palmetto', {
        tier: 2,
        why: 'Cochrane meta-analysis confirms saw palmetto significantly improves urinary symptoms associated with BPH. This is Grade A evidence for a meaningful clinical outcome. The mechanism involves 5-alpha-reductase inhibition and anti-inflammatory activity in prostate tissue.',
        note: 'Physician evaluation is appropriate if urinary symptoms are significant — this supports, not replaces, medical care.',
      })],
    });
  }

  // ── MODULE 12: SKIN & HAIR ───────────────────────────────────────────
  if (p.wantsSkin || p.wantsHair) {
    const skinProducts = [];


    if (p.wantsJoint) {
    }


    modules.push({
      id: 'skin',
      title: 'Skin, Hair & Collagen Support',
      icon: '✨',
      rationale: 'Your profile indicates skin or hair health as a priority. The nutritional drivers of both overlap — biotin, collagen precursors, and anti-inflammatory support address the underlying structural factors rather than surface symptoms.',
      products: (() => {
        if (lifePlusTier !== null) {
          skinProducts.push(card('life-plus', {
            tier: lifePlusTier,
            why: p.wantsHair
              ? 'Life+ supports skin, hair, and nails through the collagen lifecycle — bioactive collagen peptides signal collagen renewal, hyaluronic acid supports skin hydration and firmness, and zinc and biotin directly support hair strength and nail health. A structural approach to beauty from within.'
              : 'Life+ addresses skin collagen at the lifecycle level — protecting existing collagen, stimulating renewal, and building the hydrated environment it needs to remain firm and resilient. The Collagen/HA Matrix has clinical evidence for measurable improvements in skin collagen density and visible reduction in aging markers.',
            note: 'Poultry-derived (hydrolyzed chicken sternal cartilage) — not appropriate for vegetarians or vegans. Not recommended during pregnancy or nursing.',
          }));
        }
        return skinProducts;
      })(),
    });
  }

  // ── MODULE 13: METABOLIC / BLOOD SUGAR ──────────────────────────────
  if (p.bloodSugar || p.wantsWeight) {
    const metProducts = [];

    if (p.bloodSugar) {
      metProducts.push(card('glucose-regulation', {
        tier: 2,
        why: 'You indicated blood sugar management as a concern. Chromium polynicotinate at 400mcg (1,143% DV) supports insulin sensitivity through a well-characterized mechanism, paired with banaba leaf extract standardized to 18% corosolic acid for a complementary glucose-uptake pathway. Magnesium, taurine, vanadium, and alpha-lipoic acid round out the metabolic support panel.',
        note: '⚠ IMPORTANT: Chromium and vanadium can have additive effects with diabetes medications and may contribute to hypoglycemia when combined. Mandatory physician consultation if you take any blood sugar medication before adding this.',
      }));
    }

    if (p.wantsWeight) {

    }

    if (metProducts.length > 0) {
      modules.push({
        id: 'metabolic',
        title: 'Metabolic Health & Body Composition',
        icon: '⚗️',
        rationale: p.bloodSugar
          ? 'Blood sugar regulation is a central driver of energy, inflammatory load, and long-term metabolic trajectory. These products address the most evidence-supported nutritional contributors — through mechanisms that complement dietary and lifestyle changes rather than replacing them.'
          : 'Body composition goals are most effectively supported through adequate protein, strategic supplementation, and the movement and dietary practices in your lifestyle guidance. These products address the metabolic support dimension.',
        products: metProducts,
      });
    }
  }

  // ── MODULE 14: EYE HEALTH ────────────────────────────────────────────
  if (p.wantsEye || p.is65plus) {
    const eyeProducts = [
      card('eye-health', {
        tier: 2,
        why: 'Lutein and zeaxanthin are selectively concentrated in the macular pigment. The AREDS2 trial — the most rigorous eye health RCT conducted — showed 25% reduction in AMD progression risk with lutein/zeaxanthin supplementation. This is among the most specific and well-evidenced supplement applications for organ protection.',
      }),
    ];

    if (!stripIncludes.has('carotomax')) {
      eyeProducts.push(card('carotomax', {
        tier: 2,
        why: 'Full carotenoid spectrum — lutein, zeaxanthin, beta-carotene, and lycopene — addressing the complete range of eye-protective antioxidants. CarotoMax provides the carotenoid breadth that EyeHealth\'s targeted lutein/zeaxanthin doesn\'t cover alone.',
      }));
    }

    modules.push({
      id: 'eye',
      title: 'Vision & Eye Health',
      icon: '👁',
      rationale: p.is65plus
        ? 'Eye health activates at 65+ — macular degeneration risk increases substantially with age and is one of the leading causes of preventable vision loss. AREDS2 evidence supports proactive lutein/zeaxanthin supplementation before symptoms develop.'
        : 'You identified eye health as a specific concern. Lutein and zeaxanthin have the strongest clinical evidence base for macular protection of any supplement category, with Grade A clinical trial data.',
      products: eyeProducts,
    });
  }

  // ── MODULE 15: LONGEVITY ─────────────────────────────────────────────
  if (p.wantsLongevity && !vivixAdded) {
    const vivixFormat = p.prefersGummy ? 'vivix-gummy' : 'vivix-original';
    const longevityProducts = [
      card(vivixFormat, {
        tier: 2,
        why: 'Ghanim et al. 2011 (JCEM) — randomized controlled trial showing this specific polyphenol/resveratrol combination significantly suppresses post-meal oxidative stress and inflammatory gene expression. This is the best available clinical evidence in the longevity supplement space.',
        note: vivixFormat === 'vivix-original' ? 'Requires refrigeration after opening. Highest polyphenol dose of all Vivix formats.' : null,
      }),
    ];
    if (lifePlusTier !== null) {
      longevityProducts.push(card('life-plus', {
        tier: lifePlusTier,
        why: 'Life+ addresses structural longevity — supporting the collagen scaffold that maintains joint mobility, skin resilience, bone integrity, and connective tissue throughout the body. Where Vivix works at the cellular level through polyphenol protection, Life+ works at the structural level through the full collagen lifecycle. Complementary approaches to aging well.',
        note: 'Poultry-derived (hydrolyzed chicken sternal cartilage) — not appropriate for vegetarians or vegans. Not recommended during pregnancy or nursing. Consult physician if on blood thinners.',
      }));
    }


    modules.push({
      id: 'longevity',
      title: 'Longevity & Cellular Health',
      icon: '🔬',
      rationale: 'You identified healthy aging as a top priority. Vivix addresses longevity at the cellular level — the polyphenol/resveratrol combination has Grade A RCT evidence for suppression of oxidative stress and inflammatory gene expression, two of the primary drivers of accelerated biological aging.',
      products: longevityProducts,
    });
    vivixAdded = true;
  }

  // ── MODULE 16: LIVER HEALTH ──────────────────────────────────────────
  if (p.wantsLiver) {
    modules.push({
      id: 'liver',
      title: 'Liver & Detoxification Support',
      icon: '🫁',
      rationale: 'You identified liver health as a specific concern. The liver is the primary metabolic processing organ — supporting its function has downstream effects on hormone metabolism, toxin clearance, energy, and skin health.',
      products: [card('liver-dtox', {
        tier: 2,
        why: 'Milk thistle extract 300mg is the structural anchor — silymarin has Grade A evidence for liver enzyme normalization in multiple clinical trials. The DTX Enhanced Proprietary Blend adds 900mg of schisandra, dandelion, reishi, turmeric, and artichoke — five additional herbs traditionally used for hepatic and bile support.',
        note: 'If you have a diagnosed liver condition, discuss with your physician before starting liver support supplements.',
      })],
    });
  }

  // Filter out any null products from cards() that didn't resolve
  return modules.map(m => ({
    ...m,
    products: (m.products || []).filter(Boolean),
  })).filter(m => m.products.length > 0);
}


// ─────────────────────────────────────────────────────────────────────────────
// PRACTICE SUGGESTIONS — Diet & Movement  (v1.2)
// Called from Results.jsx. Returns [{id, text}] array. Caller takes first 2-3.
// ─────────────────────────────────────────────────────────────────────────────

export function getDietSuggestions(answers) {
  const a = answers || {};
  const suggestions = [];

  const veg        = a.q6_vegetables || '';
  const fruit      = a.q6b_fruit || '';
  const lowVeg     = ['1\u20132', 'Less than 1'].includes(veg);
  const lowFruit   = ['Less than 1', 'Rarely or never'].includes(fruit);
  const upf        = a.q_upf || '';
  const highUPF    = /most|daily|frequent/i.test(upf);
  const sugar      = a.q12_sugar || '';
  const highSugar  = sugar.startsWith('Frequent') || sugar.startsWith('High');
  const protein    = Array.isArray(a.q7_protein) ? a.q7_protein : [];
  const lowProtein = protein.some(p => p.includes('not sure')) ||
    (protein.length > 0 && protein.every(p => p.includes('Plant')));
  const fish       = a.q8_fish || '';
  const lowFish    = ['A few times per month', 'Rarely or never'].includes(fish);
  const win        = a.q_window || '';
  const wideWindow = !win || /14|16|more than|no pattern|irregular/i.test(win);

  if (lowVeg && lowFruit) {
    suggestions.push({ id: 'DIET_1', text: "Your vegetable and fruit intake is below the levels associated with the health outcomes you're aiming for. The most effective starting point: add one serving of each at two meals per day — crowding in rather than cutting out. Nutrient density per calorie is where whole-food eating pays off fastest." });
  } else if (lowVeg) {
    suggestions.push({ id: 'DIET_1b', text: "A handful of greens, a side of roasted vegetables, a cup of broth-based soup — one additional vegetable serving at two meals per day is the single highest-return dietary habit for most people. Not a diet overhaul. A consistent addition." });
  } else if (lowFruit) {
    suggestions.push({ id: 'DIET_1c', text: "Fruit is the easiest produce gap to close. One piece with breakfast, one with an afternoon snack. Fructose from whole fruit behaves differently than added sugar — the fiber matrix changes absorption entirely. This one is genuinely low-effort." });
  }

  if (highUPF && suggestions.length < 3) {
    suggestions.push({ id: 'DIET_3', text: "Ultra-processed foods show up frequently in your diet. The most sustainable reduction strategy is swap-based: replace one UPF per day with a whole-food alternative. Displacement beats elimination. The goal isn't perfection — it's gradually shifting the ratio in a direction your body will notice." });
  }

  if (highSugar && !highUPF && suggestions.length < 3) {
    suggestions.push({ id: 'DIET_3b', text: "Sugar and processed foods are a frequent part of your current pattern — by your own assessment. The most effective entry point isn't restriction, it's substitution: one swap per day, not a dietary overhaul. The cumulative effect of consistent small changes is larger than most people expect." });
  }

  if (wideWindow && suggestions.length < 3) {
    suggestions.push({ id: 'DIET_2', text: "Your eating window — the hours between your first and last caloric intake — appears to be undefined or extended. Compressing it to 10\u201312 hours supports circadian metabolic signaling and gives your gut the nightly rest that microbial balance and gut lining repair depend on. This isn't intermittent fasting. It's basic meal timing hygiene." });
  }

  if (lowProtein && suggestions.length < 3) {
    suggestions.push({ id: 'DIET_4', text: "Your protein picture looks uncertain. Plant sources alone often fall short of the leucine threshold required to trigger muscle protein synthesis. Aim for 25\u201335g of complete protein at two or three meals per day, and track it for one week. The number tends to surprise people — usually in the low direction." });
  }

  if (lowFish && suggestions.length < 3) {
    suggestions.push({ id: 'DIET_5', text: "Your fatty fish intake is low. Salmon, sardines, mackerel, herring — 2\u20133 times per week is one of the most evidence-supported single dietary habits for cardiovascular and cognitive health. If fish isn't workable, your supplement protocol addresses this gap directly. But food-first is always the better starting conversation." });
  }

  if (suggestions.length === 0) {
    suggestions.push({ id: 'DIET_GEN', text: "Your dietary foundation looks solid. The highest-leverage refinement at this level is meal timing consistency — eating within a defined 10\u201312 hour window and keeping protein intake consistent across meals rather than concentrated at dinner." });
  }

  return suggestions.slice(0, 3);
}

export function getMovementSuggestions(answers) {
  const a = answers || {};
  const suggestions = [];

  const ex         = a.q13_exercise || '';
  const exType     = Array.isArray(a.q14_exercise_type) ? a.q14_exercise_type : [];
  const sport      = a.q_sport || '';
  const noStrength = !exType.some(e => e.includes('Strength'));
  const isAthlete  = ex.startsWith('Very active') || ex.startsWith('Active');
  const noSport    = !sport || sport.startsWith('No') || sport === '';

  if (ex.startsWith('Sedentary') || ex.startsWith('Lightly active')) {
    suggestions.push({ id: 'MOVE_1', text: "Walking is the most underrated health intervention available — not a placeholder for real exercise, but the real thing. 7,000\u20138,000 steps per day is associated with a 50\u201365% reduction in all-cause mortality risk, effects that dwarf most supplements. Start with 20 minutes after dinner every day for two weeks, then build from there." });
  }

  if (ex.startsWith('Very active') && suggestions.length < 3) {
    suggestions.push({ id: 'MOVE_3', text: "At your training volume, recovery is where adaptation actually happens — not during the workouts themselves. Sleep, nutrition timing, and protected rest days aren't optional for people training at your frequency. Without them, you're accumulating stress without accumulating adaptation. Your supplement protocol addresses the nutritional dimension; the behavioral piece is protecting the rest days." });
  }

  if (ex.startsWith('Moderately active') && suggestions.length < 3) {
    suggestions.push({ id: 'MOVE_4', text: "Moderate activity is a real foundation. The next leverage point isn't working harder — it's removing the gaps. Three consistent sessions per week, same days, same general time, is more valuable than five variable sessions. Habit architecture matters more than workout design at this stage." });
  }

  if (noStrength && suggestions.length < 3) {
    const txt = isAthlete
      ? "You're cardiovascularly active but resistance training isn't in the picture. After 30, muscle mass declines at roughly 1% per year without active resistance work — affecting metabolism, bone density, insulin sensitivity, and functional capacity decade by decade. Two sessions per week is enough to reverse the trend. The barrier to entry is lower than most people think."
      : "Resistance training is the single most impactful movement practice for long-term health — more so than cardio for most people who aren't already doing it. It doesn't require a gym. Two sessions per week of bodyweight or light resistance work is enough to begin changing the trajectory on muscle mass, bone density, and metabolic rate.";
    suggestions.push({ id: isAthlete ? 'MOVE_2' : 'MOVE_2b', text: txt });
  }

  if (noSport && (ex.startsWith('Sedentary') || ex.startsWith('Lightly active')) && suggestions.length < 3) {
    suggestions.push({ id: 'MOVE_5', text: "The most reliable behavior change mechanism for movement is social accountability — more reliable than motivation, more reliable than apps, more reliable than planning. A walking partner, a group class, a recreational sport. The social component shifts adherence from willpower-dependent to relationship-supported. Worth finding before trying to solve it alone." });
  }

  if (suggestions.length === 0) {
    suggestions.push({ id: 'MOVE_GEN', text: "Your movement foundation is strong. The highest-leverage next step at your activity level is adding intentional recovery — structured rest days, sleep-first thinking, and post-training nutrition within 60 minutes of finishing. Adaptation happens in the recovery window, not the training window." });
  }

  return suggestions.slice(0, 3);
}
